import Constants from "utils/Constants";
import { createSocketClient, disconnectSocket } from "utils/socket/socketClient";
import {
  fetchBytesFromUrl,
  pollExportJobStatusUntilReady,
  triggerFileDownload,
  EXCEL_STATUS_POLL_INTERVAL_MS,
} from "utils/pdfExport";

export const EXCEL_EXPORT_TIMEOUT_MS = 300000;
export const EXCEL_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Waits for a background Excel job on tbs-file-service and downloads the finished file.
 *
 * Completion is detected from the Socket.IO `job_completed_<jobId>` event, with status polling
 * running alongside as a fallback (the event is broadcast-only and can be missed). The promise
 * resolves with `{ emailed }` and rejects with an Error carrying a user-facing message.
 *
 * @param {Object} params
 * @param {{jobId: string, socketUrl: string, statusUrl?: string, downloadUrl?: string, fileName?: string}} params.job
 * @param {string} params.fallbackFileName - used when the job does not report a file name
 * @param {(message: string) => void} [params.onProgress]
 */
export const downloadBackgroundExcelJob = ({ job, fallbackFileName, onProgress }) =>
  new Promise((resolve, reject) => {
    const { jobId, socketUrl, statusUrl, downloadUrl, fileName } = job;
    const baseUrl = socketUrl.replace(/\/$/, "");
    const resolvedStatusUrl = statusUrl || `${baseUrl}/api/excel/excel-status/${jobId}`;
    const resolveDownloadUrl = (data) =>
      data?.downloadUrl || downloadUrl || `${baseUrl}/api/excel/excel-download/${jobId}`;

    const socket = createSocketClient(socketUrl);
    let settled = false;
    let completing = false;

    const settle = (error, result) => {
      if (settled) return;
      settled = true;
      disconnectSocket(socket);
      if (error) reject(error);
      else resolve(result);
    };

    const complete = async (data) => {
      if (settled || completing) return;
      completing = true;

      if (data && data.success === false) {
        settle(new Error(Constants.EXCEL_GENERATION_FAILED));
        return;
      }

      // Long-running jobs are emailed instead of being downloaded in the browser.
      if (data?.emailed || data?.emailAttempted) {
        settle(null, { emailed: true });
        return;
      }

      try {
        const bytes = await fetchBytesFromUrl(
          resolveDownloadUrl(data),
          Constants.EXCEL_GENERATION_FAILED
        );
        await triggerFileDownload(
          bytes,
          data?.fileName || fileName || fallbackFileName,
          EXCEL_CONTENT_TYPE
        );
        settle(null, { emailed: false });
      } catch (_error) {
        settle(new Error(Constants.EXCEL_GENERATION_FAILED));
      }
    };

    socket.on(`job_progress_${jobId}`, (progress) => {
      if (!settled && progress?.message) onProgress?.(progress.message);
    });
    socket.on(`job_completed_${jobId}`, complete);

    pollExportJobStatusUntilReady(resolvedStatusUrl, {
      failedMessage: Constants.EXCEL_GENERATION_FAILED,
      timeoutMessage: Constants.EXCEL_EXPORT_TIMED_OUT,
      timeoutMs: EXCEL_EXPORT_TIMEOUT_MS,
      intervalMs: EXCEL_STATUS_POLL_INTERVAL_MS,
    })
      .then(complete)
      .catch((error) => {
        settle(
          new Error(
            error?.message === Constants.EXCEL_EXPORT_TIMED_OUT
              ? Constants.EXCEL_EXPORT_TIMED_OUT
              : Constants.EXCEL_GENERATION_FAILED
          )
        );
      });
  });
